<script setup lang="ts">
import type { Instance } from 'flatpickr/dist/types/instance';

// eslint-disable-next-line import/no-extraneous-dependencies
import flatpickr from 'flatpickr';
import {
  nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'flatpickr/dist/flatpickr.min.css';

interface Emits {
  (_e: 'update:modelValue', _value: [string, string] | string | undefined): void;
}

interface Props {
  dateFormat?: string;
  disabled?: boolean;
  maxDate?: string | undefined;
  minDate?: string | undefined;
  mode?: 'range' | 'single';
  modelValue?: [string, string] | string | undefined;
  placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
  dateFormat: 'd/m/Y',
  disabled: false,
  maxDate: undefined,
  minDate: undefined,
  mode: 'range',
  modelValue: undefined,
  placeholder: 'Selecione a data',
});

const emit = defineEmits<Emits>();

let flatpickrInstance: Instance | null = null;
const inputRef = ref<HTMLInputElement | null>(null);

const initFlatpickr = () => {
  if (!inputRef.value) return;

  if (flatpickrInstance) {
    flatpickrInstance.destroy();
    flatpickrInstance = null;
  }

  const options: flatpickr.Options.Options = {
    dateFormat: props.dateFormat,
    locale: {
      firstDayOfWeek: 1,
      months: {
        longhand: [
          'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
          'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
        ],
        shorthand: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
      },
      rangeSeparator: ' - ',
      weekdays: {
        longhand: ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
        shorthand: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
      },
    },
    mode: props.mode,
    onChange: (selectedDates, dateStr) => {
      if (props.mode === 'range') {
        if (selectedDates.length === 2) {
          const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };

          const startDate = formatDate(selectedDates[0]);
          const endDate = formatDate(selectedDates[1]);
          emit('update:modelValue', [startDate, endDate]);

          if (inputRef.value && dateStr) {
            nextTick(() => {
              if (inputRef.value) {
                inputRef.value.value = dateStr.replace(/\s+to\s+/gi, ' - ');
              }
            });
          }
        } else if (selectedDates.length === 0) {
          emit('update:modelValue', undefined);
        }
      } else if (props.mode === 'single' && selectedDates.length === 1) {
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        const date = formatDate(selectedDates[0]);
        emit('update:modelValue', date);
      } else {
        emit('update:modelValue', undefined);
      }
    },
    onReady: (selectedDates, dateStr) => {
      if (props.mode === 'range' && inputRef.value && dateStr) {
        inputRef.value.value = dateStr.replace(/\s+to\s+/gi, ' - ');
      }
    },
    rangeSeparator: ' - ',
  } as flatpickr.Options.Options;

  if (props.minDate) {
    options.minDate = props.minDate;
  }

  if (props.maxDate) {
    options.maxDate = props.maxDate;
  } else {
    options.maxDate = new Date();
  }

  if (props.disabled && inputRef.value) {
    inputRef.value.disabled = true;
  }

  flatpickrInstance = flatpickr(inputRef.value, options);

  if (props.modelValue) {
    nextTick(() => {
      if (!flatpickrInstance) return;

      const parseDateString = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      };

      if (props.mode === 'range' && Array.isArray(props.modelValue)) {
        const startDate = parseDateString(props.modelValue[0]);
        const endDate = parseDateString(props.modelValue[1]);
        if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
          flatpickrInstance.setDate([startDate, endDate], false);
          nextTick(() => {
            if (inputRef.value) {
              inputRef.value.value = inputRef.value.value.replace(/\s+to\s+/gi, ' - ');
            }
          });
        }
      } else if (typeof props.modelValue === 'string') {
        const date = parseDateString(props.modelValue);
        if (!Number.isNaN(date.getTime())) {
          flatpickrInstance.setDate(date, false);
        }
      }
    });
  }
};

const destroyFlatpickr = () => {
  if (flatpickrInstance) {
    flatpickrInstance.destroy();
    flatpickrInstance = null;
  }
};

onMounted(() => {
  initFlatpickr();
});

onBeforeUnmount(() => {
  destroyFlatpickr();
});

watch(() => props.modelValue, (newValue) => {
  if (!flatpickrInstance) return;

  nextTick(() => {
    if (!flatpickrInstance) return;

    const parseDateString = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };

    if (props.mode === 'range') {
      if (Array.isArray(newValue) && newValue.length === 2) {
        const startDate = parseDateString(newValue[0]);
        const endDate = parseDateString(newValue[1]);

        if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
          flatpickrInstance.setDate([startDate, endDate], false);
          nextTick(() => {
            if (inputRef.value) {
              inputRef.value.value = inputRef.value.value.replace(/\s+to\s+/gi, ' - ');
            }
          });
        }
      } else if (!newValue) {
        flatpickrInstance.clear();
      }
    } else if (typeof newValue === 'string') {
      const date = parseDateString(newValue);
      if (!Number.isNaN(date.getTime())) {
        flatpickrInstance.setDate(date, false);
      }
    } else if (!newValue) {
      flatpickrInstance.clear();
    }
  });
}, { immediate: false });

watch(() => props.disabled, (newValue) => {
  if (inputRef.value) {
    inputRef.value.disabled = newValue;
  }
});
</script>

<template>
  <input
    ref="inputRef"
    type="text"
    class="form-control"
    :placeholder="placeholder"
    :disabled="disabled"
    aria-label="Seletor de data"
    readonly
  />
</template>

<style scoped lang="scss">
:deep(.flatpickr-calendar) {
  font-family: var(--tblr-font-sans-serif);
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

:deep(.flatpickr-day.selected),
:deep(.flatpickr-day.startRange),
:deep(.flatpickr-day.endRange) {
  background: var(--tblr-primary, #00B569);
  border-color: var(--tblr-primary, #00B569);
}

:deep(.flatpickr-day.selected.inRange),
:deep(.flatpickr-day.startRange.inRange),
:deep(.flatpickr-day.endRange.inRange) {
  background: rgba(0, 181, 105, 0.1);
  border-color: rgba(0, 181, 105, 0.1);
}

:deep(.flatpickr-day.inRange) {
  background: rgba(0, 181, 105, 0.1);
  border-color: rgba(0, 181, 105, 0.1);
  box-shadow: none;
}

:deep(.flatpickr-day:hover) {
  background: rgba(0, 181, 105, 0.2);
  border-color: rgba(0, 181, 105, 0.2);
}
</style>
